import React, { useEffect, useState } from "react";
import { api, fmt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, GoldButton, Card, EmptyState, ui } from "@/components/Primitives";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, PiggyBank, Target } from "lucide-react";

const blank = { name: "", target_amount: "", current_amount: "", target_date: "", note: "" };

export default function Savings() {
  const { user } = useAuth();
  const cur = user?.currency || "AUD";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const load = () => api.get("/goals").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...it }); setOpen(true); };
  const save = async () => {
    if (!form.name) return toast.error("Name is required");
    const payload = { ...form, target_amount: Number(form.target_amount || 0), current_amount: Number(form.current_amount || 0) };
    try {
      if (editing) await api.put(`/goals/${editing.id}`, payload);
      else await api.post("/goals", payload);
      toast.success(editing ? "Updated" : "Goal created"); setOpen(false); load();
    } catch { toast.error("Could not save"); }
  };
  const remove = async (id) => { await api.delete(`/goals/${id}`); toast.success("Removed"); load(); };

  return (
    <div>
      <PageHeader title="Savings & Goals" subtitle="Set gentle targets and watch your progress grow."
        action={<GoldButton data-testid="add-goal-btn" onClick={openNew}><Plus size={16} /> New Goal</GoldButton>} />

      {items.length === 0 ? (
        <Card><EmptyState icon={PiggyBank} title="No goals yet" hint="Create a goal — an emergency fund, a home deposit, a dream holiday." /></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((g, i) => {
            const pct = g.target_amount > 0 ? Math.min(100, (g.current_amount / g.target_amount) * 100) : 0;
            return (
              <Card key={g.id} className="fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`goal-card-${g.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#F0E7D8] flex items-center justify-center"><Target size={18} className="text-[#C6A45C]" /></div>
                    <div>
                      <div className="font-display text-lg text-[#2C2A28]">{g.name}</div>
                      {g.target_date && <div className="text-xs text-[#A99F90]">by {g.target_date}</div>}
                    </div>
                  </div>
                  <div className="flex">
                    <button data-testid={`edit-goal-${g.id}`} onClick={() => openEdit(g)} className="text-[#8A7F70] hover:text-[#C6A45C] p-1.5"><Pencil size={15} /></button>
                    <button data-testid={`delete-goal-${g.id}`} onClick={() => remove(g.id)} className="text-[#8A7F70] hover:text-[#C99FA0] p-1.5"><Trash2 size={15} /></button>
                  </div>
                </div>
                <div className="flex items-end justify-between mb-2">
                  <span className="font-display text-2xl font-light tabular-nums text-[#2C2A28]">{fmt(g.current_amount, cur)}</span>
                  <span className="text-sm text-[#A99F90]">of {fmt(g.target_amount, cur)}</span>
                </div>
                <Progress value={pct} className="h-2 bg-[#EFE6D8] [&>div]:bg-[#C6A45C]" />
                <div className="flex justify-between mt-2 text-xs">
                  <span className="gold-text">{pct.toFixed(0)}% funded</span>
                  <span className="text-[#A99F90]">{fmt(Math.max(0, g.target_amount - g.current_amount), cur)} to go</span>
                </div>
                {g.note && <p className="text-xs text-[#A99F90] mt-3 italic">{g.note}</p>}
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className={ui.dialog}>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-light">{editing ? "Edit Goal" : "New Savings Goal"}</DialogTitle>
            <DialogDescription className="text-[#8A7F70]">Give your goal a name and a target.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className={ui.label}>Goal name</Label>
              <Input data-testid="goal-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={ui.input} placeholder="Emergency Fund" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className={ui.label}>Target ({cur})</Label>
                <Input data-testid="goal-target" type="number" value={form.target_amount} onChange={(e) => setForm({ ...form, target_amount: e.target.value })} className={ui.input} placeholder="20000" />
              </div>
              <div>
                <Label className={ui.label}>Saved so far</Label>
                <Input data-testid="goal-current" type="number" value={form.current_amount} onChange={(e) => setForm({ ...form, current_amount: e.target.value })} className={ui.input} placeholder="5000" />
              </div>
            </div>
            <div>
              <Label className={ui.label}>Target date</Label>
              <Input data-testid="goal-date" type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} className={ui.input} />
            </div>
            <div>
              <Label className={ui.label}>Note</Label>
              <Input data-testid="goal-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={ui.input} placeholder="Optional" />
            </div>
          </div>
          <DialogFooter>
            <GoldButton data-testid="save-goal-btn" onClick={save} className="w-full">{editing ? "Save Changes" : "Create Goal"}</GoldButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
